import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // --- EXPORT STATE ---
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const [activeTab, setActiveTab] = useState('revenue'); 
  const [restaurant, setRestaurant] = useState(null);
  
  // --- KPI STATE ---
  const [revenueStats, setRevenueStats] = useState({ 
    today: 0, month: 0, year: 0, total: 0, 
    countToday: 0, countMonth: 0, countYear: 0, countTotal: 0,
    aov: 0 
  });
  
  const [recentOrders, setRecentOrders] = useState([]); 
  const [revenueGraph, setRevenueGraph] = useState([]);
  
  const [engagementStats, setEngagementStats] = useState({ 
    totalViews: 0, totalInteractions: 0, cartConversion: 0 
  });
  
  const [popularItems, setPopularItems] = useState([]);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const { data: resto } = await supabase.from('restaurants').select('*').eq('owner_id', session.user.id).single();

      if (resto) {
        setRestaurant(resto);
        const promises = [fetchRevenueData(resto.id)];
        if (resto.is_pro) {
          promises.push(fetchEngagementData(resto.id));
        }
        await Promise.all(promises);
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- FETCHERS ---
  const fetchRevenueData = async (restoId) => {
    // Fetch ALL orders to calculate stats locally
    const { data: orders } = await supabase
      .from('orders')
      .select('id, total_price, created_at, table_number, status')
      .eq('restaurant_id', restoId)
      .order('created_at', { ascending: false });

    if (!orders) return;

    // 1. Set Recent Orders (Top 5 only)
    setRecentOrders(orders.slice(0, 5));

    // 2. Calculate KPIs (Only Completed)
    const completedOrders = orders.filter(o => o.status === 'Completed');
    
    const now = new Date();
    let s = { 
      today: 0, month: 0, year: 0, total: 0, 
      countToday: 0, countMonth: 0, countYear: 0, countTotal: 0,
      aov: 0
    };
    
    const last7Days = {};
    for (let i = 6; i >= 0; i--) { 
      const d = new Date(); d.setDate(d.getDate() - i); 
      last7Days[d.toDateString()] = 0; 
    }

    completedOrders.forEach(order => {
      const date = new Date(order.created_at);
      const price = parseFloat(order.total_price);

      s.total += price; 
      s.countTotal++;

      if (date.getFullYear() === now.getFullYear()) {
        s.year += price; s.countYear++;
        if (date.getMonth() === now.getMonth()) {
          s.month += price; s.countMonth++;
          if (date.toDateString() === now.toDateString()) { 
            s.today += price; s.countToday++; 
          }
        }
      }

      const dateStr = date.toDateString();
      if (last7Days.hasOwnProperty(dateStr)) last7Days[dateStr] += price;
    });

    s.aov = s.countTotal > 0 ? (s.total / s.countTotal) : 0;
    setRevenueStats(s);
    setRevenueGraph(Object.keys(last7Days).map(d => ({ 
      day: new Date(d).toLocaleDateString('en-US', { weekday: 'short' }), 
      amount: last7Days[d] 
    })));
  };

  const fetchEngagementData = async (restoId) => {
    const { data: events } = await supabase.from('analytics_events').select('event_type, item_id').eq('restaurant_id', restoId);
    const { data: items } = await supabase.from('menu_items').select('id, name, image_url').eq('restaurant_id', restoId);
    
    if (!events) return;

    const itemMap = {}; 
    items?.forEach(i => itemMap[i.id] = i);
    
    let views = 0, interactions = 0, itemCounts = {};

    events.forEach(e => {
      if (e.event_type === 'scan') views++;
      if (e.event_type === 'add_to_cart') {
        interactions++;
        if (e.item_id) itemCounts[e.item_id] = (itemCounts[e.item_id] || 0) + 1;
      }
    });

    setEngagementStats({ 
      totalViews: views, 
      totalInteractions: interactions, 
      cartConversion: views > 0 ? ((interactions/views)*100).toFixed(1) : 0
    });

    setPopularItems(Object.entries(itemCounts)
      .map(([id, count]) => ({ ...itemMap[id], count }))
      .sort((a,b)=>b.count-a.count)
      .slice(0,5));
  };

  // --- SMART EXPORT HANDLER ---
  const handleExport = async (range) => {
    setShowExportModal(false);
    setExporting(true);

    try {
      let query = supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', restaurant.id)
        .order('created_at', { ascending: false });

      // Apply Filter
      const now = new Date();
      if (range === 'month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        query = query.gte('created_at', startOfMonth);
      } else if (range === 'year') {
        const startOfYear = new Date(now.getFullYear(), 0, 1).toISOString();
        query = query.gte('created_at', startOfYear);
      }
      // 'lifetime' needs no filter

      const { data: orders, error } = await query;
      if (error) throw error;

      if (!orders || orders.length === 0) {
        alert("No orders found for this period.");
        setExporting(false);
        return;
      }

      // Calculate Total Revenue (Completed Only)
      const totalRevenue = orders.reduce((sum, order) => {
        return order.status === 'Completed' ? sum + parseFloat(order.total_price) : sum;
      }, 0);

      const headers = ['Order ID', 'Date', 'Time', 'Table No', 'Status', 'Amount (INR)'];
      
      const csvRows = [
        headers.join(','), 
        ...orders.map(order => {
          const dateObj = new Date(order.created_at);
          return [
            order.id,
            dateObj.toLocaleDateString(),
            dateObj.toLocaleTimeString(),
            order.table_number || 'N/A',
            order.status,
            order.total_price
          ].join(',');
        })
      ];

      csvRows.push(',,,,,'); 
      csvRows.push(`,,,,TOTAL REVENUE,${totalRevenue}`);

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${restaurant.name.replace(/\s+/g, '_')}_${range.toUpperCase()}_Report.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export data.");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  
  if (loading) return <div className="h-screen flex items-center justify-center font-bold text-gray-400">Loading Analytics...</div>;

  const isPro = restaurant?.is_pro;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-900">
      
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 px-8 py-6 flex justify-between items-center sticky top-0 z-10">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">Analytics Dashboard</h1>
          <p className="text-gray-500 text-sm">Overview for <span className="font-bold text-gray-900">{restaurant?.name}</span></p>
        </div>
        
        <div className="flex gap-3">
          {isPro && (
            <button 
              onClick={() => setShowExportModal(true)} 
              disabled={exporting} 
              className="flex items-center gap-2 bg-green-50 text-green-700 hover:bg-green-100 px-4 py-2 rounded-lg font-bold text-sm transition-colors border border-green-200"
            >
              {exporting ? <span>⏳ Exporting...</span> : <><span>⬇️</span> Export CSV</>}
            </button>
          )}
          <button onClick={() => navigate('/dashboard')} className="text-gray-500 hover:text-black font-bold text-sm flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
            <span>←</span> Back
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="px-8 pt-6 bg-white border-b border-gray-100">
        <div className="flex gap-6">
          <button onClick={() => setActiveTab('revenue')} className={`pb-4 px-2 font-bold text-sm transition-all relative ${activeTab === 'revenue' ? 'text-[#ee5b2b]' : 'text-gray-400 hover:text-gray-600'}`}>💰 Revenue & Sales {activeTab === 'revenue' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ee5b2b] rounded-t-full"></div>}</button>
          <button onClick={() => setActiveTab('engagement')} className={`pb-4 px-2 font-bold text-sm transition-all relative flex items-center gap-2 ${activeTab === 'engagement' ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>📊 Customer Insights {!isPro && <span className="bg-gray-100 text-gray-500 border border-gray-200 text-[10px] px-1.5 py-0.5 rounded font-bold">PRO</span>} {activeTab === 'engagement' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t-full"></div>}</button>
        </div>
      </div>

      <div className="p-8 max-w-7xl mx-auto w-full space-y-8 animate-fade-in pb-20">
        
        {/* === TAB 1: REVENUE === */}
        {activeTab === 'revenue' && (
          <>
            {/* KPI CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 group hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4"><div className="p-3 bg-green-50 rounded-xl text-green-600 text-xl">💵</div><span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-1 rounded-full">+Today</span></div>
                <div className="text-3xl font-bold text-gray-900">{formatCurrency(revenueStats.today)}</div>
                <p className="text-xs text-gray-400 mt-1 font-medium">{revenueStats.countToday} orders today</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 group hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4"><div className="p-3 bg-blue-50 rounded-xl text-blue-600 text-xl">📅</div><span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Month</span></div>
                <div className="text-3xl font-bold text-gray-900">{formatCurrency(revenueStats.month)}</div>
                <p className="text-xs text-gray-400 mt-1 font-medium">{revenueStats.countMonth} orders this month</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 group hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4"><div className="p-3 bg-purple-50 rounded-xl text-purple-600 text-xl">🛒</div><span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Avg. Order</span></div>
                <div className="text-3xl font-bold text-gray-900">{formatCurrency(revenueStats.aov)}</div>
                <p className="text-xs text-gray-400 mt-1 font-medium">Average spend per customer</p>
              </div>
              <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-2xl shadow-lg border border-gray-800 text-white">
                <div className="flex justify-between items-start mb-4"><div className="p-3 bg-white/10 rounded-xl text-white text-xl">🏆</div><span className="text-[10px] font-bold bg-white/20 text-white px-2 py-1 rounded-full">Lifetime</span></div>
                <div className="text-3xl font-bold">{formatCurrency(revenueStats.total)}</div>
                <p className="text-xs text-gray-400 mt-1 font-medium">{revenueStats.countTotal} total completed orders</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* REVENUE CHART */}
              <div className="lg:col-span-2 bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                 <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   📈 Revenue Trend <span className="text-gray-400 text-xs font-normal bg-gray-100 px-2 py-1 rounded">Last 7 Days</span>
                 </h3>
                 <div className="h-64 flex items-end gap-4">
                    {revenueGraph.map((d, i) => {
                      const maxVal = Math.max(...revenueGraph.map(g => g.amount), 100); 
                      const heightPercent = (d.amount / maxVal) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center group cursor-pointer">
                          <div className="relative w-full flex justify-center items-end h-full bg-gray-50 rounded-t-lg rounded-b-md overflow-hidden transition-colors">
                            <div className="w-full bg-gradient-to-t from-orange-500 to-orange-400 opacity-80 group-hover:opacity-100 transition-all rounded-t-md" style={{ height: `${heightPercent}%`, minHeight: d.amount > 0 ? '6px' : '0' }}></div>
                            <div className="absolute -top-10 bg-gray-900 text-white text-[10px] font-bold py-1.5 px-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10 whitespace-nowrap">{formatCurrency(d.amount)}</div>
                          </div>
                          <span className="text-xs font-bold text-gray-400 mt-3 group-hover:text-gray-800">{d.day}</span>
                        </div>
                      )
                    })}
                 </div>
              </div>

              {/* RECENT TRANSACTIONS (5 Orders Only) */}
              <div className="bg-white p-0 rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900">Recent Orders</h3>
                  <p className="text-xs text-gray-500">Last 5 transactions</p>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {recentOrders.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No recent orders found.</div>
                  ) : (
                    <table className="w-full text-left">
                      <tbody className="divide-y divide-gray-50">
                        {recentOrders.map((order) => {
                          const isCompleted = order.status === 'Completed';
                          const isCancelled = order.status === 'Cancelled' || order.status === 'Rejected';
                          
                          return (
                            <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                              <td className="p-4">
                                <p className="text-xs font-bold text-gray-900">#{String(order.id).slice(0,8)}</p>
                                <p className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleDateString()}</p>
                              </td>
                              <td className="p-4">
                                <p className="text-xs font-medium text-gray-500">Table {order.table_number || '-'}</p>
                              </td>
                              <td className="p-4">
                                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                                  isCompleted ? 'bg-green-100 text-green-700' : 
                                  isCancelled ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {order.status}
                                </span>
                              </td>
                              <td className="p-4 text-right">
                                <span className={`text-xs font-bold px-2 py-1 rounded-md ${
                                  isCompleted ? 'text-green-600 bg-green-50' : 
                                  isCancelled ? 'text-red-600 bg-red-50 line-through' : 'text-gray-600 bg-gray-50'
                                }`}>
                                  {formatCurrency(order.total_price)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

            </div>
          </>
        )}

        {/* === TAB 2: ENGAGEMENT (PRO LOCKED) === */}
        {activeTab === 'engagement' && (
          !isPro ? (
            <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
               <div className="p-6 bg-blue-50 text-blue-500 rounded-2xl mb-6 text-4xl">📊</div>
               <h2 className="text-2xl font-bold text-gray-900 mb-2">Unlock Customer Insights</h2>
               <p className="text-gray-500 max-w-md text-center mb-8">
                 See who views your menu, which items they add to cart, and analyze your conversion funnel to boost sales.
               </p>
               <button className="bg-black text-white px-8 py-3 rounded-xl font-bold shadow-xl hover:scale-105 transition-transform">
                 Upgrade to Pro 🚀
               </button>
            </div>
          ) : (
            <>
              {/* FUNNEL VIEW */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                 {/* Visual Funnel */}
                 <div className="lg:col-span-2 bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">🎯 Conversion Funnel</h3>
                    
                    <div className="space-y-8">
                      <div className="relative">
                        <div className="flex justify-between text-sm font-bold text-gray-600 mb-2">
                          <span>Menu Views (Scans)</span>
                          <span>{engagementStats.totalViews}</span>
                        </div>
                        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: '100%' }}></div>
                        </div>
                      </div>

                      <div className="relative pl-8">
                         <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                         <div className="flex justify-between text-sm font-bold text-gray-600 mb-2">
                          <span>Items Added to Cart</span>
                          <span className="text-blue-600">{engagementStats.cartConversion}% Conv.</span>
                        </div>
                        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(engagementStats.cartConversion, 100)}%` }}></div>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{engagementStats.totalInteractions} interactions</p>
                      </div>
                    </div>
                 </div>

                 {/* Key Metrics Cards */}
                 <div className="grid grid-cols-1 gap-6">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-center">
                       <span className="text-4xl mb-3">👀</span>
                       <h4 className="text-sm font-bold text-gray-500 uppercase">Total Views</h4>
                       <p className="text-3xl font-bold text-gray-900">{engagementStats.totalViews}</p>
                       <p className="text-xs text-gray-400">Unique QR scans</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-center">
                       <span className="text-4xl mb-3">⚡</span>
                       <h4 className="text-sm font-bold text-gray-500 uppercase">Conversion Rate</h4>
                       <p className="text-3xl font-bold text-gray-900">{engagementStats.cartConversion}%</p>
                       <p className="text-xs text-gray-400">View to Cart Ratio</p>
                    </div>
                 </div>
              </div>

              {/* POPULAR ITEMS TABLE (RESTORED) */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-6">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">🔥 Most Popular Dishes</h3>
                    <p className="text-xs text-gray-500">Items most frequently added to cart.</p>
                  </div>
                </div>
                {popularItems.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">Not enough data to rank items yet.</div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase w-16">Rank</th>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase">Dish Details</th>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase text-right">Interest</th>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase text-right">Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popularItems.map((item, i) => {
                         const maxCount = popularItems[0].count;
                         const percent = (item.count / maxCount) * 100;
                         
                         return (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                            <td className="p-4 font-bold text-gray-400">#{i+1}</td>
                            <td className="p-4 flex items-center gap-3">
                              <img src={item.image_url || 'https://via.placeholder.com/40'} className="w-10 h-10 rounded-lg object-cover bg-gray-100 border border-gray-200" alt=""/>
                              <span className="font-bold text-gray-900">{item.name}</span>
                            </td>
                            <td className="p-4 text-right font-bold text-blue-600">{item.count} Adds</td>
                            <td className="p-4 w-32">
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${percent}%` }}></div>
                              </div>
                            </td>
                          </tr>
                         );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )
        )}
      </div>

      {/* EXPORT MODAL */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-slide-up">
             <div className="flex justify-between items-center mb-6">
                <div>
                   <h3 className="text-xl font-bold text-gray-900">Export Data</h3>
                   <p className="text-sm text-gray-500">Choose a date range.</p>
                </div>
                <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-black">✕</button>
             </div>
             
             <div className="space-y-3">
               <button onClick={() => handleExport('month')} className="w-full p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-900 text-left font-bold transition-all flex justify-between group">
                 <span>📅 This Month</span>
                 <span className="text-gray-400 group-hover:text-black">→</span>
               </button>
               <button onClick={() => handleExport('year')} className="w-full p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-900 text-left font-bold transition-all flex justify-between group">
                 <span>🗓️ This Year</span>
                 <span className="text-gray-400 group-hover:text-black">→</span>
               </button>
               <button onClick={() => handleExport('lifetime')} className="w-full p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-900 text-left font-bold transition-all flex justify-between group">
                 <span>♾️ Lifetime</span>
                 <span className="text-gray-400 group-hover:text-black">→</span>
               </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;